import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/profileService';

interface UseLastReadReturn {
  lastReadId: number | null;
  updateLastRead: (id: number) => void;
}

export function useLastRead(): UseLastReadReturn {
  const { user } = useAuth();
  const [lastReadId, setLastReadId] = useState<number | null>(() => {
    const savedId = user?.frontend_state?.wsj_feed_last_read_id;
    return typeof savedId === 'number' ? savedId : null;
  });
  const pendingIdRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  // Flush pending update to server
  const flush = useCallback(async () => {
    const id = pendingIdRef.current;
    if (id === null) return;
    pendingIdRef.current = null;
    try {
      await profileService.saveFrontendState({ wsj_feed_last_read_id: id });
    } catch {
      // Silently fail - will retry on next flush
    }
  }, []);

  // Flush on unmount and visibility change
  useEffect(() => {
    isMountedRef.current = true;

    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        flush();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      flush();
    };
  }, [flush]);

  // Only update if the new ID is greater (newer message)
  const updateLastRead = useCallback((id: number) => {
    setLastReadId(prev => {
      if (prev !== null && id <= prev) return prev;
      pendingIdRef.current = id;
      return id;
    });
  }, []);

  return { lastReadId, updateLastRead };
}
